import { useEffect } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import ContactForm from "@/components/site/ContactForm";
import { BRAND } from "@/data/brand";

const ACCENT = "#004D40"; // teal

export default function Contact() {
  useReveal();
  useEffect(() => { document.title = "Contact — LifeIQ"; }, []);

  const scrollToForm = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main>
      {/* HERO */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#F57F17" }}>Get in touch</div>
            <h1 className="mt-5 font-serif-d text-[42px] sm:text-6xl lg:text-[64px] leading-[1.05] tracking-tight">
              Let's start with a conversation — not a pitch.
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              Whether you lead a school, teach in one, are a parent who wants this for your child's school, or simply believe young people deserve this — we'd genuinely like to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="contact-form" className="bg-white">
        <div className="max-w-[820px] mx-auto px-5 sm:px-8 py-24 md:py-28">
          <div className="text-center reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Write to us</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[46px] leading-[1.1]">
              Tell us a little, and we'll reach out.
            </h2>
          </div>

          <div className="mt-12 rounded-sm border border-[#E5E7EA] bg-white p-7 sm:p-10 shadow-[0_4px_30px_rgba(13,27,42,0.04)] reveal">
            <ContactForm
              idPrefix="contact-page"
              theme="light"
              roles={["School leader", "Teacher", "Parent", "Investor or partner", "Other"]}
              roleLabel="I am a…"
              rolePlaceholder="Select"
              schoolLabel="School name"
              schoolPlaceholder="(optional)"
              submitLabel="Send Message"
              source="contact-page"
            />
          </div>

          <p className="mt-6 text-center text-[14px] text-[#5C6470] reveal">
            We read every message personally and reply within two working days.
          </p>
        </div>
      </section>

      {/* DIRECT DETAILS */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-28">
          <div className="text-center reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Direct details</div>
            <h2 className="mt-5 font-serif-d text-[30px] sm:text-4xl lg:text-[42px] leading-[1.1]">
              Or reach us directly.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7 text-center">
              <div className="w-11 h-11 rounded-full mx-auto flex items-center justify-center text-white" style={{ backgroundColor: ACCENT }}>
                <Mail size={18} />
              </div>
              <div className="mt-5 text-[12px] uppercase tracking-[0.22em] text-[#5C6470] font-semibold">Email</div>
              <a href={`mailto:${BRAND.email}`} className="mt-2 inline-block font-serif-d text-[20px] text-[#0D1B2A] hover:underline">{BRAND.email}</a>
            </div>
            <div className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7 text-center" style={{ transitionDelay: "80ms" }}>
              <div className="w-11 h-11 rounded-full mx-auto flex items-center justify-center text-white" style={{ backgroundColor: ACCENT }}>
                <Phone size={18} />
              </div>
              <div className="mt-5 text-[12px] uppercase tracking-[0.22em] text-[#5C6470] font-semibold">Phone</div>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="mt-2 inline-block font-serif-d text-[20px] text-[#0D1B2A] hover:underline">{BRAND.phone}</a>
            </div>
            <div className="reveal bg-white border border-[#E5E7EA] rounded-sm p-7 text-center" style={{ transitionDelay: "160ms" }}>
              <div className="w-11 h-11 rounded-full mx-auto flex items-center justify-center text-white" style={{ backgroundColor: ACCENT }}>
                <MapPin size={18} />
              </div>
              <div className="mt-5 text-[12px] uppercase tracking-[0.22em] text-[#5C6470] font-semibold">Based in</div>
              <div className="mt-2 font-serif-d text-[20px] text-[#0D1B2A]">{BRAND.city}</div>
              <div className="mt-1 text-[14px] text-[#5C6470]">Serving the twin cities first.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-28 text-center">
          <div className="reveal">
            <h2 className="font-serif-d text-[36px] sm:text-5xl lg:text-[54px] leading-[1.08] text-white max-w-3xl mx-auto">
              One honest pilot is all it takes to know.
            </h2>
            <p className="mt-7 text-[17px] sm:text-[19px] text-white/80 leading-[1.7] max-w-2xl mx-auto">
              We're not looking to convince anyone with words. We're looking for one forward-thinking school willing to try it for a single term — and let the results speak. If that's you, this is where it begins.
            </p>
            <button
              type="button"
              onClick={scrollToForm}
              data-testid="contact-closing-cta"
              className="mt-10 inline-flex items-center gap-2 bg-[#F57F17] text-white px-8 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm"
            >
              Book a Pilot Conversation
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
